import { EMPTY, VISUAL_TITLE_DEFAULT_FONT_SIZE } from "@zebrabi/constants";
import {
    extractFontName,
    formatQuillFontSize,
    getValidFontSize,
    isEmptyDelta,
    MAX_QUILL_FONT_SIZE,
    MIN_QUILL_FONT_SIZE,
    toFontFamilyValue,
    toQuillFontSizeUnit,
} from "@zebrabi/ui-components";
import type { QuillDelta, QuillOpAttributes } from "@zebrabi/ui-components";
import { parseVisualTitleContentIntoDelta } from "@zebrabi/visual-title";

/**
 * The legacy title fields this module reads - shared by charts and tables, since both persist the title the same way
 */
export interface LegacyTitleSettings {
    titleText: string;
    titleFontFamily: string;
    titleFontSize: number;
    fontSizeUnit?: string;
    visualTitleDelta: QuillDelta;
}

/**
 * Reduces an Office font list to the single font name a Quill delta stores.
 *
 * extractFontName only reads a list whose first font is quoted ("'Segoe UI', ..."), so two kinds of list need help:
 * - DIN fonts, which start with an internal alias rather than a name ("wf_standard-font, ...") -> toFontFamilyValue
 * - unquoted lists like the default "Calibri, ..." -> the first item is already the name
 *
 * @param fontFamily the persisted font list, e.g. "Calibri, helvetica, arial, sans-serif"
 * @returns the Quill font value, with whitespace hyphenated ("Segoe UI" -> "Segoe-UI"), or undefined when nothing
 * matches - which leaves the title in Segoe UI
 */
function getTitleQuillFontName(fontFamily: string): string | undefined {
    const fontList = fontFamily ?? EMPTY;
    const firstFont = fontList.split(",")[0].replace(/['"]/g, EMPTY).trim();
    return extractFontName(fontList) ?? extractFontName(toFontFamilyValue(fontList)) ?? extractFontName(firstFont);
}

/**
 * Corrects a title's font for Office, which persists font families as unquoted lists rather than the quoted ones
 * the shared helpers expect.
 *
 * The size needs no correction: the editor authors font sizes in the host's own unit, so a size the visual mapped
 * from settings already carries "pt" and keeps the size the user set.
 *
 * @param baseAttributes the visual's own title attributes - charts and tables each map their own settings
 * @param titleFontFamily the persisted title font list
 * @returns a copy of baseAttributes with the font corrected for office
 */
export function withOfficeTitleFont(baseAttributes: QuillOpAttributes, titleFontFamily: string): QuillOpAttributes {
    return { ...baseAttributes, font: getTitleQuillFontName(titleFontFamily) };
}

/**
 * Sizes a legacy title, capped to the range the editor offers.
 *
 * Quill drops a size that is not on its whitelist, which would leave the title at the editor's default rather than
 * anywhere near the size it was saved at. Capping means a title above the range shrinks once, on migration, instead
 * of losing its size entirely.
 *
 * @param settings the persisted title settings
 * @returns the size attribute, in the unit the host renders text in
 */
function getLegacyTitleSize(settings: LegacyTitleSettings): string {
    const fontSize = getValidFontSize(settings.titleFontSize, VISUAL_TITLE_DEFAULT_FONT_SIZE);

    return formatQuillFontSize({
        size: Math.min(Math.max(fontSize, MIN_QUILL_FONT_SIZE), MAX_QUILL_FONT_SIZE),
        unit: toQuillFontSizeUnit(settings.fontSizeUnit),
    });
}

/**
 * Converts a legacy titleText into a visualTitleDelta, in place.
 *
 * The Quill delta format (visualTitleDelta) replaced titleText, so titles saved by older instances need converting
 * or they are lost. Once the new format is saved, conversion is skipped.
 *
 * @param settings must not be null, and is mutated in place - visualTitleDelta is set and titleText cleared
 * @param baseAttributes the visual's own title attributes, corrected for office before the delta is built
 */
export function convertLegacyTitleToDelta(settings: LegacyTitleSettings, baseAttributes: QuillOpAttributes): void {
    const isDeltaTitleStored = !!settings.visualTitleDelta;
    const legacyTitleText = settings.titleText;
    const isLegacyTitleValid = String(legacyTitleText ?? EMPTY).trim().length > 0;

    if (isDeltaTitleStored || !isLegacyTitleValid) {
        return;
    }

    const attributes = { ...withOfficeTitleFont(baseAttributes, settings.titleFontFamily), size: getLegacyTitleSize(settings) };
    const convertedQuillTitle = parseVisualTitleContentIntoDelta(legacyTitleText, [], attributes);

    if (isEmptyDelta(convertedQuillTitle)) {
        return;
    }

    settings.visualTitleDelta = convertedQuillTitle;
    // clear the legacy field so a later title reset (visualTitleDelta = null) is not re-converted
    settings.titleText = EMPTY;
}
